import React from 'react';
import { useParams, Redirect } from 'react-router-dom';
import { Paper, Divider, Grid } from '@material-ui/core';
import { LoadingSpinner, Box, Text } from '@bighealth/react-limbix-ui';

import AssessmentResultDisplay from './AssessmentResultDisplay';

import { formatDate } from '@/utils/dateUtils';
import { QueryAssignmentArgs } from '@/apollo/types';
import { ASSIGNMENT_QUERY } from '@/apollo/queries';
import { UseParamsType } from '@/types';
import { renderPID } from '@/utils/tableUtils';
import { useQuery } from '@/hooks/apollo';
import { usePermissions } from '@/hooks/redux';

const Details: React.FC = () => {
  const { studyUid, assignmentUid } = useParams<UseParamsType>();
  const { loading, data, error } = useQuery<QueryAssignmentArgs>(ASSIGNMENT_QUERY, {
    variables: {
      studyUid,
      assignmentUid,
    },
  });
  const [{ studyPermissions }] = usePermissions();
  const permissions = studyPermissions[studyUid];

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error?.message === 'Assignment matching that UID does not exist') {
    return <Redirect to="/research" />;
  }

  const { assignment, assignment: { participantAssessmentResult } } = data;

  return (
    <Box width="100%" display="flex" justifyContent="center">
      <Box width="80%" position="relative">
        <Paper elevation={2} style={{ width: '100%' }}>
          <Box width="100%" height="100%" padding="30px">
            <Text fontWeight="700" fontSize="22px">
              Assignment Details
            </Text>
            <Divider />
            <Box padding="15px">
              <Grid
                container
                justifyContent="space-between"
                alignItems="stretch"
                style={{ height: '100%' }}
              >
                <Grid item xs={3}>
                  <Box>
                    <Text fontWeight="700" fontSize="18px">
                      Assessment:
                    </Text>
                    <Text>
                      {assignment.assessment?.name || 'Adverse Event Questionnaire'}
                    </Text>
                  </Box>
                  <Box marginTop="20px">
                    <Text fontWeight="700" fontSize="18px">
                      Visible Date:
                    </Text>
                    <Text>
                      {formatDate(assignment.visibleDate)}
                    </Text>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box>
                    <Text fontWeight="700" fontSize="18px">
                      Status:
                    </Text>
                    <Text>
                      {assignment.status}
                    </Text>
                  </Box>
                  <Box marginTop="20px">
                    <Text fontWeight="700" fontSize="18px">
                      Due Date:
                    </Text>
                    <Text>
                      {formatDate(assignment.dueDate)}
                    </Text>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box>
                    <Text fontWeight="700" fontSize="18px">
                      User:
                    </Text>
                    <Text>
                      {renderPID(
                        studyUid,
                        assignment.studyParticipant.user.uid,
                        assignment.studyParticipant.participantId,
                      )}
                    </Text>
                  </Box>
                  <Box marginTop="20px">
                    <Text fontWeight="700" fontSize="18px">
                      Start Time:
                    </Text>
                    <Text>
                      {formatDate(assignment.startTime)}
                    </Text>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box>
                    <Text fontWeight="700" fontSize="18px">
                      About User:
                    </Text>
                    <Text>
                      {renderPID(
                        studyUid,
                        assignment?.aboutStudyParticipant?.user?.uid,
                        assignment?.aboutStudyParticipant?.participantId,
                      )}
                    </Text>
                  </Box>
                  <Box marginTop="20px">
                    <Text fontWeight="700" fontSize="18px">
                      Complete Time:
                    </Text>
                    <Text>
                      {formatDate(assignment.completeTime)}
                    </Text>
                  </Box>
                </Grid>
              </Grid>
            </Box>
            {participantAssessmentResult && (
              <AssessmentResultDisplay
                questions={participantAssessmentResult?.assessment?.questions}
                assessmentAnswers={participantAssessmentResult?.assessmentResultItems}
                score={participantAssessmentResult?.score}
                completeTime={assignment.completeTime}
                isBlinded={permissions.isBlinded}
              />
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default Details;
